import * as THREE from "three";
import { fieldSurface } from "../scene/fieldSurface.js";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

/** Actual roadside geometry around the combat lanes; all scenery stays outside formation bounds. */
export class FieldEnvironment {
  readonly group = new THREE.Group();
  private geometry = new Set<THREE.BufferGeometry>();
  private materials = new Set<THREE.Material>();
  private disposed = false;
  constructor() {
    this.group.name = "battle-roadside";
    const concrete = this.mat(0x777361),
      steel = this.mat(0x303d3d, 0.65),
      rust = this.mat(0x65452c, 0.3),
      yellow = this.mat(0x9b753a),
      glass = this.mat(0x172d33, 0.3),
      rubber = this.mat(0x182021),
      earth = fieldSurface(0x6b6449, true);
    this.materials.add(earth);
    this.box(100, 0.12, 100, 0, -0.25, 0, earth);
    const road = fieldSurface(0x373d3d, true);
    this.materials.add(road);
    this.box(16, 0.1, 90, 0, -0.1, 0, road);
    for (const side of [-1, 1]) {
      this.box(0.25, 0.2, 72, side * 8, 0, 0, concrete);
      for (let z = -38; z <= 38; z += 4) {
        this.box(0.12, 0.01, 1.5, side * 0.13, 0.012, z, yellow);
      }
      // Wire fencing: open silhouette, braced posts and a few missing sections.
      for (let z = -18; z <= 18; z += 3) {
        this.box(0.09, 1.5, 0.09, side * 10, 0.7, z, steel);
        if (z === 0 || z === 3) continue;
        for (const h of [0.4, 0.9, 1.4]) this.box(0.025, 0.025, 3, side * 10, h, z + 1.5, rust);
        for (let j = 0; j < 6; j++) {
          const mesh = this.box(0.025, 1.48, 0.025, side * 10, 0.7, z + j * 0.5, steel);
          mesh.rotation.x = 0.23;
        }
      }
    }
    // Broken concrete barricades, each with a distinct chipped edge and reflectors.
    for (let i = 0; i < 7; i++) {
      const side = i % 2 === 0 ? -1 : 1,
        z = -10 + Math.floor(i / 2) * 6.2;
      const b = this.box(0.6, 0.65, 1.8, side * 7, 0.27, z, concrete);
      b.rotation.y = ((i % 3) - 0.8) * 0.12;
      this.box(0.63, 0.13, 0.26, side * 7, 0.42, z - 0.45, yellow);
      this.box(0.63, 0.13, 0.26, side * 7, 0.42, z + 0.45, yellow);
      const debris = this.box(0.3, 0.18, 0.45, side * 6.45, 0.03, z + 0.8, concrete);
      debris.rotation.set(0.13, i, 0.2);
    }
    // Abandoned utility transport: visible glazing, wheel wells, ribs and a crushed hood.
    const bus = new THREE.Group();
    bus.position.set(-6.9, 0, -4.8);
    bus.rotation.y = 0.16;
    this.group.add(bus);
    this.box(2.1, 1.3, 5.5, 0, 0.96, 0, yellow, bus);
    this.box(2.04, 0.09, 5.3, 0, 1.65, 0, rust, bus);
    this.box(1.85, 0.65, 0.08, 0, 1.21, -2.78, glass, bus);
    for (const side of [-1, 1])
      for (let j = 0; j < 5; j++)
        this.box(0.045, 0.55, 0.66, side * 1.06, 1.22, -1.75 + j * 0.81, glass, bus);
    for (const side of [-1, 1])
      for (const z of [-1.72, 1.72]) {
        const geo = new THREE.CylinderGeometry(0.44, 0.44, 0.25, 14);
        this.geometry.add(geo);
        const wheel = new THREE.Mesh(geo, rubber);
        wheel.rotation.z = Math.PI / 2;
        wheel.position.set(side * 1.02, 0.43, z);
        bus.add(wheel);
        this.box(0.05, 0.2, 0.2, side * 1.18, 0.43, z, steel, bus);
      }
    this.box(2.2, 0.22, 0.17, 0, 0.4, -2.88, steel, bus);
    const hood = this.box(1.96, 0.13, 0.7, 0, 0.85, -2.65, rust, bus);
    hood.rotation.x = -0.19;
    // Bounded instanced rubble and crossed dry grass, no decorative draw call per blade.
    const rubbleGeo = new THREE.IcosahedronGeometry(0.18, 0);
    this.geometry.add(rubbleGeo);
    const rubble = new THREE.InstancedMesh(rubbleGeo, concrete, 100);
    rubble.name = "battle-rubble";
    const grassGeo = new THREE.BufferGeometry();
    grassGeo.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(
        [-0.18, 0, 0, 0, 0.55, 0, 0.18, 0, 0, 0, 0, -0.18, 0, 0.42, 0, 0, 0, 0.18],
        3,
      ),
    );
    grassGeo.computeVertexNormals();
    this.geometry.add(grassGeo);
    const grassMat = this.mat(0x716f43);
    grassMat.side = THREE.DoubleSide;
    const grass = new THREE.InstancedMesh(grassGeo, grassMat, 160);
    grass.name = "battle-verge";
    const tr = new THREE.Object3D();
    for (let i = 0; i < 160; i++) {
      const side = i % 2 === 0 ? -1 : 1;
      tr.position.set(side * (8.4 + ((i * 1.731) % 5)), 0.02, -22 + ((i * 3.171) % 44));
      tr.rotation.set(0, i * 1.31, 0);
      tr.scale.setScalar(0.5 + ((i * 0.327) % 1));
      tr.updateMatrix();
      grass.setMatrixAt(i, tr.matrix);
      if (i < 100) {
        tr.position.x += 0.4;
        tr.rotation.set(i, 0.7 * i, 0.3 * i);
        tr.scale.set(1, 0.5, 1.4);
        tr.updateMatrix();
        rubble.setMatrixAt(i, tr.matrix);
      }
    }
    grass.instanceMatrix.needsUpdate = true;
    rubble.instanceMatrix.needsUpdate = true;
    this.group.add(grass, rubble);
    // Static scenery becomes one draw per material, not hundreds of individual props.
    this.group.updateMatrixWorld(true);
    const batches = new Map<THREE.Material, THREE.BufferGeometry[]>(),
      sources: THREE.Mesh[] = [];
    this.group.traverse((object) => {
      if (
        object instanceof THREE.Mesh &&
        !(object instanceof THREE.InstancedMesh) &&
        !Array.isArray(object.material)
      ) {
        const list = batches.get(object.material) ?? [];
        list.push(object.geometry.clone().applyMatrix4(object.matrixWorld));
        batches.set(object.material, list);
        sources.push(object);
      }
    });
    for (const object of sources) {
      object.removeFromParent();
      object.geometry.dispose();
      this.geometry.delete(object.geometry);
    }
    for (const [material, parts] of batches) {
      const geometry = mergeGeometries(parts);
      for (const part of parts) part.dispose();
      if (geometry) {
        this.geometry.add(geometry);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        this.group.add(mesh);
      }
    }
    const worklight = new THREE.PointLight(0xffd09a, 65, 23, 2);
    worklight.position.set(-4, 5, -2);
    this.group.add(worklight);
    const fill = new THREE.DirectionalLight(0xc2d7e2, 0.65);
    fill.position.set(3, 7, 5);
    this.group.add(fill, fill.target);
  }
  private mat(color: number, metalness = 0) {
    const m = new THREE.MeshStandardMaterial({ color, roughness: 0.88, metalness });
    this.materials.add(m);
    return m;
  }
  private box(
    w: number,
    h: number,
    d: number,
    x: number,
    y: number,
    z: number,
    material: THREE.Material,
    parent = this.group,
  ) {
    const g = new THREE.BoxGeometry(w, h, d);
    this.geometry.add(g);
    const m = new THREE.Mesh(g, material);
    m.position.set(x, y, z);
    m.castShadow = true;
    m.receiveShadow = true;
    parent.add(m);
    return m;
  }
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.group.traverse((o) => {
      if (o instanceof THREE.InstancedMesh) o.dispose();
    });
    for (const g of this.geometry) g.dispose();
    for (const m of this.materials) m.dispose();
    this.group.removeFromParent();
    this.group.clear();
  }
}
